import type { IPractice } from "@common-shared/types/responseData/commonResponseData.types.ts"

import { GENERAL_CONFIG } from "../config/general.config"
import { ROLES_PERMISSIONS_CONFIG } from "../config/rolesPermissions.config"
import { ROUTES_CONFIG } from "../config/routes.config"
import { AuthService } from "../services/auth.service"
import type { IInviteOutsideAllowedDomainsConfirmationParams } from "../types/practice.types.ts"

export class OrganizationInviteHelper {
    /**
     * True when the invite should show an extra confirmation: practice has no allowed-domain
     * configuration, or the email's domain is not in the configured list.
     */
    static doesInviteRequireDomainConfirmation(
        practice: IPractice,
        email: string,
    ): boolean {
        const domainToCheck: string | undefined = email
            .toLowerCase()
            .split("@")[1]

        if (!domainToCheck) {
            return false
        }

        const allowed = practice.allowedUserAccountDomains

        if (!allowed?.length) {
            return true
        }

        return !allowed.some(item => item === domainToCheck)
    }

    /**
     * Gets the label for the invite role for confirmation.
     */
    static getInviteRoleLabelForConfirmation(
        roleKey: string,
        roleLabel?: string | null,
    ): string {
        const match = ROLES_PERMISSIONS_CONFIG.inviteRenderedRoles.find(
            item => item.value === roleKey,
        )

        return match?.displayValue ?? roleLabel ?? roleKey
    }

    /**
     * Builds the confirmation lines for the invite outside allowed domains confirmation.
     */
    static buildInviteOutsideAllowedDomainsConfirmationLines(
        params: IInviteOutsideAllowedDomainsConfirmationParams,
    ): string[] {
        const domains = params.allowedDomains?.join(", ")

        const footer = domains
            ? `This address is not in the allowed domains (${domains}). Send the invite anyway?`
            : "No allowed domains are configured for this practice. Send the invite anyway?"

        return [
            `Email: ${params.email}`,
            `Role: ${params.roleLabel}`,
            `Groups: ${params.groupsDisplay}`,
            "",
            footer,
        ]
    }

    /**
     * Invite acceptance URL for copying: same shape as Mailgun `signupUrl` / `loginUrl`
     * in the invite email. Prefer the current browser origin; fall back to configured domain.
     * Uses {@link AuthService.emailAvailable} like the register flow; existing users get login,
     * everyone else signup (see `sendInvitationEmail` on the invite provider).
     */
    static async getOrganizationInviteAcceptanceUrl(
        email: string,
        inviteToken: string,
    ): Promise<string> {
        const origin =
            typeof window !== "undefined"
                ? window.location.origin
                : GENERAL_CONFIG.frontAppDomain.replace(/\/$/, "")

        const result = await AuthService.emailAvailable(email, {
            ignoreEmailTakenErrorToast: true,
        })

        const emailAvailable = Boolean(result?.available)

        const params = new URLSearchParams({
            [GENERAL_CONFIG.urlSearchParamsKeys.inviteToken]: inviteToken,
            ...(emailAvailable
                ? { [GENERAL_CONFIG.urlSearchParamsKeys.email]: email }
                : {}),
        })

        const path = emailAvailable
            ? ROUTES_CONFIG.registerUrl
            : ROUTES_CONFIG.loginUrl

        return `${origin}${path}?${params.toString()}`
    }
}
